// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * A DSA 1.8 vector layer on the browser globe (MapLibre, globe projection).
 *
 * Points — the many, short-lived features (quakes, alerts) — are one static
 * GeoJSON source whose animation runs through feature-state, so a frame
 * costs a `setFeatureState` per feature that changed, never a re-tile.
 * MapLibre draws every feature of a source, shown or not, so an instant
 * layer of more than POINT_SOURCE_CAP points (95,000 yearly quakes) holds
 * only a window of them around the playhead, plus whatever is still drawn,
 * and moves it on with one re-tile every few seconds of playback. Their
 * size is a small circle on the sphere for `deg`/`km` units: the diameter
 * becomes pixels from the globe's scale at the view centre each frame
 * (`circle-pitch-alignment: map` lays the disc on the surface), clamped by
 * `minPx`/`maxPx`. Emphasis draws on more circle layers (two ripple rings,
 * a halo); a pulse swells the point itself.
 *
 * Lines, polygons and tracks are few and change shape as they draw in or as
 * a track's head moves, so their source is rebuilt — only on frames where
 * something about them changed. Paths are subdivided to ≤ 1° in lon/lat and
 * cut at the antimeridian (vectorGeo.ts); MapLibre cannot draw past ±180.
 *
 * Labels (`style.label`, vectorLabels.ts) are a symbol layer on top: one
 * image per box (text and colour), anchored at the head or point and moved
 * by `icon-translate` to clear the widest head drawn: to the right of it on
 * the left half of the globe, to the left on the right half, so a box never
 * runs off the globe's edge, where space is drawn over it. Being in the
 * map's own canvas, they are in Studio renders too, and the globe hides the
 * ones on its far side.
 *
 * Re-adds itself after a basemap change replaces the style.
 */
import type { GeoJSONSource, Map as MaplibreMap, PointLike } from 'maplibre-gl'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import { kmPerDegOf, type CompiledVectorLayer, type LonLat, type VectorFeature } from './vectorModel'
import { cutTrack, partialPath, splitAtAntimeridian, subdivide, unwrap } from './vectorGeo'
import { drawLabelBox, labelImageKey, labelsOf } from './vectorLabels'
import { evalColor, evalNumber, type Rgba } from './vectorStyle'
import type { VectorDrawItem } from './vectorTimeline'
import type { VectorSize } from './vectorTypes'

const DEFAULT_COLOR: Rgba = [1, 0.55, 0.1, 1]
/** Gap between a head (or point) and its label box, CSS px. */
const LABEL_GAP_PX = 4
/**
 * Most points an instant layer's source holds (see above). On an integrated
 * GPU 95,000 yearly quakes played at 24 fps against 60 for the video alone.
 */
export const POINT_SOURCE_CAP = 8000
/** Tap reach around the pointer, CSS px. */
export const VECTOR_PICK_REACH_PX = { mouse: 6, touch: 14 }

let counter = 0

/** A MapLibre layer this module added (`vector-<n>-<order>-…`), for the renderer to keep above the dataset. */
export function isVectorMapLayerId(id: string): boolean {
  return /^vector-\d+-\d+-/.test(id)
}

export interface VectorMapLayerHandle {
  readonly layer: CompiledVectorLayer
  /** Draws this frame's items; `dataTime` (Unix s) moves a large point layer's source window. */
  update(items: VectorDrawItem[], dataTime?: number): void
  /** The drawn feature under a screen point, for tap-to-inspect. */
  pick(point: { x: number; y: number }, reachPx: number): VectorFeature | null
  /** AYNI (owner, 2026-10-12): the dataset's transparency and eye (0: hidden, nothing to pick). */
  setUserOpacity(opacity: number): void
  remove(): void
}

function css(c: Rgba): string {
  return `rgba(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)},${+c[3].toFixed(3)})`
}

/** Screen px per degree of arc at the view centre. */
function pxPerDegree(map: MaplibreMap): number {
  const c = map.getCenter()
  const lat = Math.max(-80, Math.min(80, c.lat))
  const a = map.project([c.lng, lat - 0.5])
  const b = map.project([c.lng, lat + 0.5])
  const d = Math.hypot(a.x - b.x, a.y - b.y)
  return Number.isFinite(d) && d > 0 ? d : 1
}

/** px per size unit: the factor a `vectorSize` value is multiplied by. */
function unitPx(size: VectorSize | undefined, pxPerDeg: number, kmPerDeg: number): number {
  if (!size || size.units === 'px') return 1
  return size.units === 'deg' ? pxPerDeg : pxPerDeg / kmPerDeg
}

interface Precomputed {
  color: string
  stroke: string
  size: number
  opacity: number
  z: number
}

export function createVectorMapLayer(getMap: () => MaplibreMap | null, layer: CompiledVectorLayer, lang: () => string = () => 'en'): VectorMapLayerHandle {
  const id = `vector-${++counter}-${layer.order}`
  // DSA 1.9 §2: km sizes on the layer's body (the Earth's unless the DSA names another).
  const kmPerDeg = kmPerDegOf(layer)
  const style = layer.spec.style
  const fields = layer.spec.fields
  const geometry = layer.spec.geometry
  const isPoint = geometry === 'point'
  const ptSource = `${id}-pts`
  const geoSource = `${id}-geo`
  const hasLabels = !!style.label
  const lblSource = `${id}-lbl`
  const lblLayer = `${id}-label`
  /** Boxes left of their head, for the globe's right half. */
  const lblLayerL = `${id}-label-l`
  const layerIds = [
    ...(isPoint ? [`${id}-halo`, `${id}-pt`, `${id}-ring`, `${id}-ring2`] : [`${id}-fill`, `${id}-line`, `${id}-head`]),
    ...(hasLabels ? [lblLayer, lblLayerL] : []),
  ]

  // Per-feature style, evaluated once (§7). A null colour or size: not drawn.
  const pre = new Map<number, Precomputed>()
  const sizeSpec = geometry === 'polygon' ? style.stroke?.width : style.size
  for (const f of layer.features) {
    const color = evalColor(style.color, f.attrs, fields, DEFAULT_COLOR)
    const size = evalNumber(sizeSpec?.value, f.attrs, geometry === 'polygon' ? 1 : 4)
    const opacity = evalNumber(style.opacity, f.attrs, 1)
    if (!color || size === null || opacity === null) continue
    const stroke = evalColor(style.stroke?.color, f.attrs, fields, [0, 0, 0, 0]) ?? [0, 0, 0, 0]
    const zf = style.zOrder?.field
    const zv = zf && typeof f.attrs[zf] === 'number' ? (f.attrs[zf] as number) : 0
    pre.set(f.index, { color: css(color), stroke: css(stroke), size, opacity, z: style.zOrder?.descending ? -zv : zv })
  }
  const minPx = sizeSpec?.minPx ?? 0
  const maxPx = sizeSpec?.maxPx ?? 10000
  // A track's head: its own size units and clamps, else the line's (§7.6).
  const headSpec = style.track?.head?.size ?? sizeSpec
  const headMinPx = headSpec?.minPx ?? minPx
  const headMaxPx = headSpec?.maxPx ?? maxPx
  const fillOpacity = style.fill?.opacity ?? 1
  const strokeOpacity = style.stroke?.opacity ?? 1
  const strokePx = style.stroke?.width && style.stroke.width.units === 'px' && typeof style.stroke.width.value === 'number'
    ? style.stroke.width.value : 0
  /** AYNI (owner, 2026-10-12): the layer's own opacity times the dataset's transparency (its eye: 0). */
  let userOpacity = 1
  let layerOpacity = layer.layerOpacity
  let lastItems: VectorDrawItem[] = []

  let attached: MaplibreMap | null = null
  let lastFactor = NaN
  /** Last feature-state written per point: g (size), a (opacity), rs/ro ring, hs/ho halo. */
  const states = new Map<number, string>()
  let lastGeoKey = ''
  let lastLabelKey = ''
  let lastTranslate = NaN
  /** Label box images on the attached map: picture key → image name. */
  const images = new Map<string, string>()
  let imageCounter = 0
  /** Features currently drawn, for picking. */
  const drawn = new Map<number, VectorFeature>()

  const radius = (scale: unknown[]) => ['*', scale, ['max', minPx / 2, ['min', maxPx / 2, ['*', 0.5, ['get', 's']]]]]

  // The source window of a large instant point layer: its points in time order, the indices
  // the source holds, and the data times outside which it is rebuilt.
  const timed = isPoint && layer.spec.time?.mode === 'instant'
    ? layer.features.filter((f) => pre.has(f.index) && f.point && Number.isFinite(f.time)).sort((a, b) => a.time! - b.time!)
    : []
  const windowed = timed.length > POINT_SOURCE_CAP
  const inSource = new Set<number>()
  let windowFrom = -Infinity
  let windowUntil = Infinity

  /** Fill `inSource` with the points around data time T (a quarter before, the rest after) and `keep`. */
  function placeWindow(T: number, keep: Iterable<number>): void {
    let lo = 0
    let hi = timed.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (timed[mid].time! < T) lo = mid + 1
      else hi = mid
    }
    const from = Math.max(0, lo - POINT_SOURCE_CAP / 4)
    const to = Math.min(timed.length, from + POINT_SOURCE_CAP)
    inSource.clear()
    for (let i = from; i < to; i++) inSource.add(timed[i].index)
    for (const i of keep) inSource.add(i)
    // Rebuild before the playhead runs out of points ahead, or when it goes back before the window.
    const ahead = Math.min(to - 1, lo + Math.floor((to - lo) / 2))
    windowFrom = from > 0 ? timed[from].time! : -Infinity
    windowUntil = to < timed.length ? timed[Math.max(lo, ahead)].time! : Infinity
  }
  if (windowed) placeWindow(-Infinity, [])

  function pointData(): FeatureCollection {
    const features: Feature[] = []
    for (const f of layer.features) {
      const p = pre.get(f.index)
      if (!p || !f.point || (windowed && !inSource.has(f.index))) continue
      features.push({ type: 'Feature', id: f.index, geometry: { type: 'Point', coordinates: f.point }, properties: { c: p.color, sc: p.stroke, s: p.size, o: p.opacity, z: p.z } })
    }
    return { type: 'FeatureCollection', features }
  }

  /** The point layers' opacity paints, at the opacity now (set again when it changes). */
  function opacityPaints(): Record<'halo' | 'pt' | 'ring' | 'ring2', Record<string, never>> {
    const fs = (k: string, d = 0) => ['coalesce', ['feature-state', k], d]
    return {
      halo: { 'circle-opacity': ['*', layerOpacity, fs('ho')] as never },
      pt: {
        'circle-opacity': ['*', layerOpacity * fillOpacity, ['get', 'o'], fs('a')] as never,
        'circle-stroke-opacity': ['*', layerOpacity * strokeOpacity, fs('a')] as never,
      },
      ring: { 'circle-stroke-opacity': ['*', layerOpacity, fs('ro')] as never },
      ring2: { 'circle-stroke-opacity': ['*', layerOpacity, fs('ro2')] as never },
    }
  }

  function addLayers(map: MaplibreMap): void {
    if (isPoint) {
      map.addSource(ptSource, { type: 'geojson', data: pointData() })
      const fs = (k: string, d = 0) => ['coalesce', ['feature-state', k], d]
      map.addLayer({
        id: `${id}-halo`, type: 'circle', source: ptSource,
        paint: {
          'circle-pitch-alignment': 'map', 'circle-blur': 1,
          'circle-radius': radius(fs('hs')) as never, 'circle-color': ['get', 'c'],
          ...opacityPaints().halo,
        },
      })
      map.addLayer({
        id: `${id}-pt`, type: 'circle', source: ptSource,
        layout: { 'circle-sort-key': ['get', 'z'] },
        paint: {
          'circle-pitch-alignment': 'map',
          'circle-radius': radius(fs('g')) as never,
          'circle-color': ['get', 'c'],
          'circle-stroke-width': ['case', ['>', fs('g'), 0], strokePx, 0] as never,
          'circle-stroke-color': ['get', 'sc'],
          ...opacityPaints().pt,
        },
      })
      // Two ripple rings: they overlap (vectorTimeline's emphasis).
      for (const [ring, s] of [['ring', 'rs'], ['ring2', 'rs2']] as const) {
        map.addLayer({
          id: `${id}-${ring}`, type: 'circle', source: ptSource,
          paint: {
            'circle-pitch-alignment': 'map',
            'circle-radius': radius(fs(s)) as never, 'circle-color': 'rgba(0,0,0,0)',
            'circle-stroke-width': 1.5, 'circle-stroke-color': ['get', 'c'],
            ...opacityPaints()[ring],
          },
        })
      }
    } else {
      map.addSource(geoSource, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addLayer({
        id: `${id}-fill`, type: 'fill', source: geoSource, filter: ['==', ['get', 'k'], 'fill'],
        paint: { 'fill-color': ['get', 'c'], 'fill-opacity': ['get', 'a'] },
      })
      map.addLayer({
        id: `${id}-line`, type: 'line', source: geoSource, filter: ['==', ['get', 'k'], 'line'],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'z'] },
        paint: {
          'line-color': ['get', 'c'], 'line-opacity': ['get', 'a'],
          'line-width': ['max', minPx, ['min', maxPx, ['*', 1, ['get', 'w']]]],
        },
      })
      map.addLayer({
        id: `${id}-head`, type: 'circle', source: geoSource, filter: ['==', ['get', 'k'], 'head'],
        paint: { 'circle-radius': ['*', 0.5, ['max', headMinPx, ['min', headMaxPx, ['get', 'w']]]], 'circle-color': ['get', 'c'], 'circle-opacity': ['get', 'a'], 'circle-stroke-width': 1, 'circle-stroke-color': 'rgba(0,0,0,0.5)', 'circle-stroke-opacity': ['get', 'a'] },
      })
    }
    if (hasLabels) {
      map.addSource(lblSource, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      for (const [layerId, side, anchor] of [[lblLayer, 'r', 'left'], [lblLayerL, 'l', 'right']] as const) {
        map.addLayer({
          id: layerId, type: 'symbol', source: lblSource, filter: ['==', ['get', 's'], side],
          layout: {
            'icon-image': ['get', 'img'], 'icon-anchor': anchor,
            'icon-allow-overlap': true, 'icon-ignore-placement': true,
            'icon-pitch-alignment': 'viewport', 'icon-rotation-alignment': 'viewport',
            'symbol-sort-key': ['get', 'z'],
          },
          paint: { 'icon-opacity': ['get', 'a'], 'icon-translate-anchor': 'viewport', 'icon-translate-transition': { duration: 0, delay: 0 } },
        })
      }
    }
    states.clear()
    lastGeoKey = ''
    lastFactor = NaN
    lastLabelKey = ''
    lastTranslate = NaN
    images.clear()
  }

  const onStyle = () => { if (attached) ensure(attached) }

  function ensure(map: MaplibreMap): boolean {
    if (!map.isStyleLoaded?.() && !map.getSource(isPoint ? ptSource : geoSource)) {
      // A style mid-load cannot take sources yet; the next frame retries.
      try { if (!map.getStyle()) return false } catch { return false }
    }
    if (!map.getSource(isPoint ? ptSource : geoSource)) {
      try { addLayers(map) } catch { return false }
    }
    return true
  }

  function currentMap(): MaplibreMap | null {
    const m = getMap()
    if (m !== attached) {
      attached?.off('styledata', onStyle)
      attached = m
      attached?.on('styledata', onStyle)
      states.clear()
      lastGeoKey = ''
    }
    return attached && ensure(attached) ? attached : null
  }

  /** The size factors (px per unit) for this zoom, written only when the scale moves. */
  function applyFactor(map: MaplibreMap): void {
    const ppd = pxPerDegree(map)
    const k = unitPx(sizeSpec, ppd, kmPerDeg)
    if (!Number.isFinite(lastFactor) || Math.abs(ppd - lastFactor) > Math.max(0.01, lastFactor * 0.005)) {
      lastFactor = ppd
      const fs = (key: string) => ['coalesce', ['feature-state', key], 0]
      const r = (scale: unknown[]) => ['*', scale, ['max', minPx / 2, ['min', maxPx / 2, ['*', k / 2, ['get', 's']]]]]
      if (isPoint) {
        map.setPaintProperty(`${id}-pt`, 'circle-radius', r(fs('g')) as never)
        map.setPaintProperty(`${id}-ring`, 'circle-radius', r(fs('rs')) as never)
        map.setPaintProperty(`${id}-ring2`, 'circle-radius', r(fs('rs2')) as never)
        map.setPaintProperty(`${id}-halo`, 'circle-radius', r(fs('hs')) as never)
      } else {
        map.setPaintProperty(`${id}-line`, 'line-width', ['max', minPx, ['min', maxPx, ['*', k, ['get', 'w']]]] as never)
        const kHead = unitPx(headSpec, ppd, kmPerDeg)
        map.setPaintProperty(`${id}-head`, 'circle-radius', ['*', 0.5, ['max', headMinPx, ['min', headMaxPx, ['*', kHead, ['get', 'w']]]]] as never)
      }
    }
  }

  function updatePoints(map: MaplibreMap, items: VectorDrawItem[], dataTime?: number): void {
    if (windowed) {
      const T = Number.isFinite(dataTime) ? dataTime! : -Infinity
      const missing = items.some((it) => pre.has(it.feature.index) && !inSource.has(it.feature.index))
      if (missing || T < windowFrom || T >= windowUntil) {
        placeWindow(T, items.map((it) => it.feature.index))
        ;(map.getSource(ptSource) as GeoJSONSource | undefined)?.setData(pointData())
      }
    }
    const seen = new Set<number>()
    for (const it of items) {
      const i = it.feature.index
      if (!pre.has(i)) continue
      seen.add(i)
      drawn.set(i, it.feature)
      let rs = 0, ro = 0, rs2 = 0, ro2 = 0, hs = 0, ho = 0
      for (const e of it.emphasis) {
        const ring = e.rings[0]
        if (!ring) continue
        if (e.effect === 'ripple') {
          rs = ring.scale; ro = ring.opacity
          if (e.rings[1]) { rs2 = e.rings[1].scale; ro2 = e.rings[1].opacity }
        } else if (e.effect === 'halo') { hs = ring.scale; ho = ring.opacity * 0.6 }
      }
      const r3 = (v: number) => +v.toFixed(3)
      const state = { g: r3(it.size), a: r3(it.opacity), rs: r3(rs), ro: r3(ro), rs2: r3(rs2), ro2: r3(ro2), hs: r3(hs), ho: r3(ho) }
      const key = `${state.g}|${state.a}|${state.rs}|${state.ro}|${state.rs2}|${state.ro2}|${state.hs}|${state.ho}`
      if (states.get(i) !== key) {
        states.set(i, key)
        map.setFeatureState({ source: ptSource, id: i }, state)
      }
    }
    for (const i of [...states.keys()]) {
      if (seen.has(i)) continue
      states.delete(i)
      drawn.delete(i)
      map.setFeatureState({ source: ptSource, id: i }, { g: 0, a: 0, rs: 0, ro: 0, rs2: 0, ro2: 0, hs: 0, ho: 0 })
    }
  }

  function lineFeatures(parts: LonLat[][], props: Record<string, unknown>): Feature | null {
    const lines = parts.flatMap((p) => splitAtAntimeridian(subdivide(unwrap(p))))
    if (!lines.length) return null
    return { type: 'Feature', geometry: { type: 'MultiLineString', coordinates: lines }, properties: props }
  }

  function updateGeo(map: MaplibreMap, items: VectorDrawItem[]): void {
    const key = `${layerOpacity}|` + items.map((it) => `${it.feature.index}:${it.opacity.toFixed(3)}:${it.size.toFixed(3)}:${it.draw.toFixed(3)}:${it.fill.toFixed(3)}:${geometry === 'track' ? `${it.headT}${it.head ? 'h' : ''}` : ''}`).join(',')
    if (key === lastGeoKey) return
    lastGeoKey = key
    drawn.clear()
    const features: Feature[] = []
    for (const it of items) {
      const f = it.feature
      const p = pre.get(f.index)
      if (!p) continue
      drawn.set(f.index, f)
      const a = +(layerOpacity * p.opacity * it.opacity).toFixed(3)
      const base = { i: f.index, z: p.z }
      if (geometry === 'polygon') {
        for (const poly of f.polygons ?? []) {
          const rings = poly.map((r) => subdivide(r))
          if (it.fill > 0 && fillOpacity > 0) {
            features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: rings } as Geometry, properties: { ...base, k: 'fill', c: p.color, a: +(a * fillOpacity * it.fill).toFixed(3) } })
          }
          const outline = it.draw < 1 ? rings.map((r) => partialPath(r, it.draw)) : rings
          const lf = lineFeatures(outline.filter((r) => r.length >= 2), { ...base, k: 'line', c: p.stroke, a: +(a * strokeOpacity).toFixed(3), w: p.size })
          if (lf && p.size > 0) features.push(lf)
        }
      } else if (geometry === 'line') {
        const parts = (f.parts ?? []).map((part) => (it.draw < 1 ? partialPath(part, it.draw) : part))
        const lf = lineFeatures(parts, { ...base, k: 'line', c: p.color, a, w: p.size * it.size })
        if (lf) features.push(lf)
      } else if (geometry === 'track' && f.parts && f.vertexTimes) {
        const track = style.track
        const window = track?.trail?.mode === 'window' ? track.trail.dataSeconds : undefined
        const cut = cutTrack(unwrap(f.parts[0]), f.vertexTimes, it.headT, track?.interpolate !== false, window)
        const perVertex = typeof style.color === 'object' && 'field' in style.color && f.perVertex[style.color.field]
        // Segments grouped by the colour of the vertex they start from (§7.6).
        let start = 0
        const colourAt = (k: number) => {
          if (!perVertex) return p.color
          const c = evalColor(style.color, { ...f.attrs, [(style.color as { field: string }).field]: perVertex[cut.from[k]] }, fields, DEFAULT_COLOR)
          return c ? css(c) : p.color
        }
        for (let k = 1; k <= cut.path.length; k++) {
          if (k === cut.path.length || colourAt(k) !== colourAt(start)) {
            const lf = lineFeatures([cut.path.slice(start, Math.min(k + 1, cut.path.length))], { ...base, k: 'line', c: colourAt(start), a, w: p.size * it.size })
            if (lf) features.push(lf)
            start = k
          }
        }
        if (cut.head && track?.head && it.head) {
          const headSize = evalNumber(track.head.size?.value, f.attrs, p.size * 3) ?? p.size * 3
          const hc = track.head.color ? evalColor(track.head.color, f.attrs, fields, DEFAULT_COLOR) : null
          const headColour = hc ? css(hc) : colourAt(Math.max(0, cut.path.length - 1))
          const lon = ((((cut.head[0] + 180) % 360) + 360) % 360) - 180
          features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lon, cut.head[1]] }, properties: { ...base, k: 'head', c: headColour, a, w: headSize * it.size } })
        }
      }
    }
    ;(map.getSource(geoSource) as GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features })
  }

  /** The labels of this frame: one box image each, anchored where vectorLabels puts them. */
  function updateLabels(map: MaplibreMap, items: VectorDrawItem[]): void {
    const labels = labelsOf(layer, items, lang())
    // The boxes clear the widest head (or point) drawn: icon-translate is one value for the layer.
    let clearPx = 0
    const ppd = pxPerDegree(map)
    for (const l of labels) {
      if (!l.clear) continue
      const min = l.clear.size?.minPx ?? 0
      const max = l.clear.size?.maxPx ?? 10000
      clearPx = Math.max(clearPx, Math.max(min, Math.min(max, l.clear.value * unitPx(l.clear.size, ppd, kmPerDeg))) / 2)
    }
    const translate = Math.round(clearPx + LABEL_GAP_PX)
    if (translate !== lastTranslate) {
      lastTranslate = translate
      map.setPaintProperty(lblLayer, 'icon-translate', [translate, 0])
      map.setPaintProperty(lblLayerL, 'icon-translate', [-translate, 0])
    }
    // Which side of its head a box goes: away from the globe's nearer edge.
    const centreX = map.project(map.getCenter()).x
    const sides = labels.map((l) => (map.project([l.lon, l.lat]).x > centreX ? 'l' : 'r'))
    // The dataset's transparency and eye reach the boxes too.
    const key = `${userOpacity}|` + labels.map((l, i) => `${l.index}:${l.lon.toFixed(3)}:${l.lat.toFixed(3)}:${l.opacity.toFixed(2)}:${sides[i]}:${labelImageKey(l)}`).join(',')
    if (key === lastLabelKey) return
    lastLabelKey = key
    const ratio = Math.min(3, Math.max(1, map.getPixelRatio?.() ?? (typeof devicePixelRatio === 'number' ? devicePixelRatio : 1)))
    const used = new Set<string>()
    const features: Feature[] = []
    labels.forEach((l, i) => {
      const pictureKey = labelImageKey(l)
      let name = images.get(pictureKey)
      if (!name || !map.hasImage(name)) {
        const box = drawLabelBox(l, ratio)
        const g = box?.canvas.getContext('2d') as CanvasRenderingContext2D | null | undefined
        if (!box || !g) return
        name = name ?? `${id}-lbl-${++imageCounter}`
        if (map.hasImage(name)) map.removeImage(name)
        map.addImage(name, g.getImageData(0, 0, box.canvas.width, box.canvas.height), { pixelRatio: ratio })
        images.set(pictureKey, name)
      }
      used.add(pictureKey)
      const zf = style.zOrder?.field
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [l.lon, l.lat] }, properties: { i: l.index, img: name, s: sides[i], a: +(l.opacity * userOpacity).toFixed(3), z: zf ? pre.get(l.index)?.z ?? 0 : 0 } })
    })
    ;(map.getSource(lblSource) as GeoJSONSource | undefined)?.setData({ type: 'FeatureCollection', features })
    // Boxes no longer shown (the wind or the name changed): their pictures go.
    for (const [pictureKey, name] of [...images]) {
      if (used.has(pictureKey)) continue
      images.delete(pictureKey)
      try { if (map.hasImage(name)) map.removeImage(name) } catch { /* style gone */ }
    }
  }

  return {
    layer,
    update(items, dataTime) {
      lastItems = items
      const map = currentMap()
      if (!map) return
      applyFactor(map)
      if (isPoint) updatePoints(map, items, dataTime)
      else updateGeo(map, items)
      if (hasLabels) updateLabels(map, items)
    },
    setUserOpacity(opacity) {
      const next = Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1
      if (next === userOpacity) return
      userOpacity = next
      layerOpacity = layer.layerOpacity * userOpacity
      const map = attached
      if (!map) return
      if (isPoint) {
        for (const [part, paints] of Object.entries(opacityPaints())) {
          const lid = `${id}-${part}`
          if (!map.getLayer(lid)) continue
          for (const [prop, value] of Object.entries(paints)) map.setPaintProperty(lid, prop as Parameters<MaplibreMap['setPaintProperty']>[1], value)
        }
      } else {
        updateGeo(map, lastItems)
      }
      if (hasLabels) updateLabels(map, lastItems)
      map.triggerRepaint()
    },
    pick(point, reachPx) {
      const map = attached
      if (!map || !drawn.size || userOpacity <= 0) return null
      const box: [PointLike, PointLike] = [[point.x - reachPx, point.y - reachPx], [point.x + reachPx, point.y + reachPx]]
      const ids = isPoint ? [`${id}-pt`, ...(hasLabels ? [lblLayer, lblLayerL] : [])] : layerIds
      let hits: Array<{ id?: string | number; properties?: Record<string, unknown> | null }> = []
      try { hits = map.queryRenderedFeatures(box, { layers: ids.filter((l) => map.getLayer(l)) }) } catch { return null }
      for (const h of hits) {
        // Points carry their index as the feature id; lines, tracks and label boxes as `i`.
        const index = h.properties?.i !== undefined ? Number(h.properties.i) : Number(h.id)
        const f = drawn.get(index)
        if (f) return f
      }
      return null
    },
    remove() {
      const map = attached
      attached?.off('styledata', onStyle)
      attached = null
      if (!map) return
      for (const l of layerIds) { try { if (map.getLayer(l)) map.removeLayer(l) } catch { /* style gone */ } }
      for (const name of images.values()) { try { if (map.hasImage(name)) map.removeImage(name) } catch { /* style gone */ } }
      images.clear()
      for (const s of [ptSource, geoSource, lblSource]) { try { if (map.getSource(s)) map.removeSource(s) } catch { /* style gone */ } }
    },
  }
}
